import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTours } from "@/hooks/useTours";
import { extractIdPrefix, tourUrl, slugify, companyUrl } from "@/lib/slugify";
import { boatTypeUrl, categoryUrl, CATEGORY_LISTINGS } from "@/lib/listingSlugs";
import { getLowestPrice, getAggregatedReview, parseRating, getCheapestPlatformIds } from "@/lib/tourUtils";
import { RATING_ATTRS, BOAT_TYPE_OPTIONS } from "@/types/tour";
import type { Tour } from "@/types/tour";
import { breadcrumbJsonLd, tourFaqJsonLd, jsonLdScript } from "@/lib/seoSchemas";
import { supabase } from "@/integrations/supabase/client";
import {
  Star,
  ExternalLink,
  Check,
  AlertTriangle,
  Circle,
  Clock,
  MapPin,
  Users,
  Ship,
  Anchor,
  ChevronRight,
  Waves,
  Images,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

function RatingCircles({ value, size = "md" }: { value: string | null; size?: "sm" | "md" }) {
  const rating = parseRating(value);
  if (rating === null) return null;
  const w = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Circle key={i} className={`${w} ${i <= rating ? "fill-primary text-primary" : "text-primary"}`} />
      ))}
    </div>
  );
}

function JsonLd({
  tour,
  price,
  review,
}: {
  tour: Tour;
  price: number | null;
  review: { rating: number; count: number } | null;
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: tour.title,
    description: tour.short_description || `${tour.title} boat tour in Aruba`,
    touristType: tour.best_for?.join(", ") || "Tourists",
    image: tour.cover_photo_url || undefined,
    provider: tour.company_name ? { "@type": "Organization", name: tour.company_name } : undefined,
    itinerary: tour.itinerary
      ? {
          "@type": "ItemList",
          itemListElement: tour.itinerary.map((step, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: step,
          })),
        }
      : undefined,
    offers: price
      ? {
          "@type": "AggregateOffer",
          lowPrice: price,
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          offerCount: tour.tour_platforms?.length || 1,
        }
      : undefined,
    aggregateRating: review
      ? {
          "@type": "AggregateRating",
          ratingValue: review.rating,
          reviewCount: review.count,
          bestRating: 5,
        }
      : undefined,
    ...(tour.departure_point && {
      location: {
        "@type": "Place",
        name: tour.departure_point + ", Aruba",
        address: { "@type": "PostalAddress", addressLocality: tour.departure_point, addressCountry: "AW" },
      },
    }),
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function SimilarTours({ current, allTours }: { current: Tour; allTours: Tour[] }) {
  const similar = useMemo(() => {
    const others = allTours.filter((t) => t.id !== current.id && t.is_active);
    const scored = others.map((t) => {
      let score = 0;
      if (t.boat_type && t.boat_type === current.boat_type) score += 3;
      if (t.categories && current.categories) {
        score += t.categories.filter((c) => current.categories!.includes(c)).length * 2;
      }
      if (t.vibe && t.vibe === current.vibe) score += 1;
      return { tour: t, score };
    });
    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((s) => s.tour);
  }, [current, allTours]);

  if (similar.length === 0) return null;

  return (
    <section className="mt-16">
      <h2 className="font-display text-2xl font-bold text-foreground mb-6">Similar Boat Tours You Might Like</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {similar.map((tour) => {
          const price = getLowestPrice(tour);
          const review = getAggregatedReview(tour);
          return (
            <Link key={tour.id} to={tourUrl(tour.id, tour.title)} className="group block">
              <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                {tour.cover_photo_url ? (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={tour.cover_photo_url}
                      alt={tour.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-muted flex items-center justify-center">
                    <Ship className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-display font-bold text-sm leading-tight line-clamp-2">{tour.title}</h3>
                  {tour.company_name && <p className="text-xs text-muted-foreground mt-1">{tour.company_name}</p>}
                  <div className="flex items-center justify-between mt-3">
                    {price !== null && <span className="font-display font-bold text-primary">From ${price}</span>}
                    {review && (
                      <span className="flex items-center gap-1 text-xs">
                        <Star className="w-3.5 h-3.5 fill-warning text-warning" />
                        <span className="font-semibold">{review.rating.toFixed(1)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function AllBoatTours({ tours, currentId }: { tours: Tour[]; currentId: string }) {
  const grouped = useMemo(() => {
    const active = tours.filter((t) => t.is_active);
    const groups: Record<string, Tour[]> = {};
    active.forEach((t) => {
      const type = t.boat_type || "Other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(t);
    });
    const order = [...BOAT_TYPE_OPTIONS, "Other"];
    return order.filter((bt) => groups[bt]).map((bt) => ({ type: bt, tours: groups[bt] }));
  }, [tours]);

  const companies = useMemo(() => {
    const active = tours.filter((t) => t.is_active && t.company_name);
    return [...new Set(active.map((t) => t.company_name!))].sort();
  }, [tours]);

  return (
    <section className="mt-16 pb-12">
      <Separator className="mb-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">All Boat Tours in Aruba</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {grouped.map(({ type, tours: groupTours }) => (
          <div key={type}>
            <Link to={boatTypeUrl(type)} className="font-display text-lg font-semibold text-foreground mb-3 flex items-center gap-2 hover:text-primary transition-colors">
              <Ship className="w-4 h-4 text-primary" />
              {type} Tours
            </Link>
            <ul className="space-y-1.5">
              {groupTours.map((t) => (
                <li key={t.id}>
                  {t.id === currentId ? (
                    <span className="text-xs text-muted-foreground font-medium">{t.title}</span>
                  ) : (
                    <Link to={tourUrl(t.id, t.title)} className="text-xs text-primary hover:underline">
                      {t.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Categories */}
      <Separator className="my-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">Aruba Boat Tours by Category</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2">
        {CATEGORY_LISTINGS.map((c) => (
          <Link key={c.slug} to={`/aruba/${c.slug}`} className="text-xs text-primary hover:underline">
            {c.value} Tours
          </Link>
        ))}
      </div>

      {/* Companies */}
      <Separator className="my-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">Boat Tour Companies in Aruba</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2">
        {companies.map((name) => (
          <Link key={name} to={companyUrl(name)} className="text-xs text-primary hover:underline">
            {name}
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function TourDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: tours, isLoading } = useTours();

  const tour = useMemo(() => {
    if (!tours || !slug) return null;
    const idPrefix = extractIdPrefix(slug);
    return tours.find((t) => t.id.startsWith(idPrefix)) || null;
  }, [tours, slug]);

  const allTours = useMemo(() => tours?.filter((t) => t.is_active) || [], [tours]);
  const price = tour ? getLowestPrice(tour) : null;
  const review = tour ? getAggregatedReview(tour) : null;
  const cheapestIds = tour ? getCheapestPlatformIds(tour) : new Set<string>();

  useEffect(() => {
    if (!tour) return;
    const desc = tour.short_description || `${tour.title} – Book the best boat tour in Aruba at the lowest price.`;
    document.title = `${tour.title} | Boat Tour in Aruba – TopCarib`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc.slice(0, 160));

    const setMeta = (prop: string, content: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(prop.startsWith("og:") ? "property" : "name", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("og:title", `${tour.title} | Boat Tour in Aruba – TopCarib`);
    setMeta("og:description", desc.slice(0, 160));
    if (tour.cover_photo_url) setMeta("og:image", tour.cover_photo_url);
    setMeta("og:type", "product");
    setMeta("og:url", `https://topcarib.com${window.location.pathname}`);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.href = `https://topcarib.com${window.location.pathname}`;

    return () => {
      document.title = "TopCarib – Helps You Find The Best Tour For The Lowest Price";
    };
  }, [tour]);

  const bookingRef = useRef<HTMLDivElement>(null);
  const [showStickyBook, setShowStickyBook] = useState(false);

  const scrollToBooking = useCallback(() => {
    bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setShowStickyBook(!entry.isIntersecting), { threshold: 0 });
    const el = bookingRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [tour]);

  const trackClick = (platformId: string) => {
    supabase
      .from("platform_clicks")
      .insert({ platform_id: platformId })
      .then(() => {});
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Waves className="w-8 h-8 animate-pulse text-primary" />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-2xl font-bold">Tour not found</h1>
        <Link to="/" className="text-primary hover:underline">
          ← Back to all tours
        </Link>
      </div>
    );
  }

  const durationDisplay =
    tour.duration_hours !== null ? (Number(tour.duration_hours) < 0 ? "Variable" : `${tour.duration_hours}h`) : null;

  // Build breadcrumb (Home → Things to do → category? → tour)
  const primaryCategory = tour.categories?.[0];
  const breadcrumb = breadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Things to do in Aruba", url: "/aruba/things-to-do" },
    ...(primaryCategory
      ? [{ name: `${primaryCategory} Tours`, url: categoryUrl(primaryCategory) }]
      : []),
    { name: tour.title, url: `/aruba/things-to-do/${slug}` },
  ]);
  const faq = tourFaqJsonLd(tour);
  const heroAlt = [tour.title, tour.boat_type && `${tour.boat_type} tour`, "in Aruba"]
    .filter(Boolean)
    .join(" – ");

  return (
    <div className="min-h-screen bg-background">
      <JsonLd tour={tour} price={price} review={review} />
      <script type="application/ld+json" dangerouslySetInnerHTML={jsonLdScript(breadcrumb)} />
      {faq && <script type="application/ld+json" dangerouslySetInnerHTML={jsonLdScript(faq)} />}

      {/* Navbar */}
      <nav className="bg-card border-b border-border px-6 lg:px-10 py-3 flex items-center justify-between sticky top-0 z-50">
        <Link to="/" className="flex items-center">
          <span className="font-display text-xl font-extrabold text-warning tracking-tight">Top</span>
          <span className="font-display text-xl font-extrabold text-primary tracking-tight">Carib</span>
        </Link>
        <Link to="/" className="text-sm text-primary hover:underline flex items-center gap-1">
          ← All tours
        </Link>
      </nav>

      <main>
      {/* Breadcrumb */}
      <div className="px-6 lg:px-10 py-3 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          Home
        </Link>
        <ChevronRight className="w-3 h-3 inline mx-1" />
        <span className="text-foreground">{tour.title}</span>
      </div>

      {/* Hero - full width */}
      <header className="px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image */}
            <div className="rounded-2xl overflow-hidden bg-muted aspect-[16/10] relative">
              {tour.cover_photo_url ? (
                <img src={tour.cover_photo_url} alt={heroAlt} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Ship className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
              {(() => {
                const priorityOrder = ["Viator", "GetYourGuide", "Expedia", "Direct booking"];
                const platform = priorityOrder
                  .map((name) => tour.tour_platforms?.find((p) => p.platform_name === name))
                  .find(Boolean);
                if (!platform) return null;
                return (
                  <a
                    href={platform.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick(platform.id)}
                    className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-background/80 backdrop-blur-sm text-foreground text-xs font-medium px-3 py-1.5 rounded-full hover:bg-background/95 transition-colors"
                  >
                    <Images className="w-3.5 h-3.5" />
                    Gallery
                  </a>
                );
              })()}
            </div>

            {/* Key info */}
            <div className="flex flex-col justify-center">
              <h1 className="font-display text-2xl md:text-4xl font-extrabold text-foreground leading-tight">
                {tour.title}
              </h1>

              {tour.company_name && (
                <p className="text-muted-foreground mt-2 text-sm">
                  by{" "}
                  <Link to={companyUrl(tour.company_name)} className="text-primary hover:underline">
                    {tour.company_name}
                  </Link>
                </p>
              )}

              {/* Rating */}
              {review && (
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-1 text-warning">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-display font-bold text-base">{review.rating.toFixed(1)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">({review.count.toLocaleString()} reviews)</span>
                </div>
              )}

              {tour.short_description && (
                <p className="text-foreground/80 mt-4 text-sm md:text-base leading-relaxed">{tour.short_description}</p>
              )}

              {/* Price */}
              {price !== null && (
                <span className="font-display font-bold text-2xl text-primary mt-4 block">From ${price}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content grid with booking sidebar */}
      <div className="px-6 lg:px-10 mt-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Booking sidebar - appears first on mobile via order */}
          <div className="lg:col-span-1 order-first lg:order-last" ref={bookingRef}>
            <div className="sticky top-20">
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="font-display text-lg font-bold text-foreground mb-1">Book This Tour</h2>
                <p className="text-xs text-muted-foreground mb-4">
                  We regularly update prices but always check each link for promotions. Some links are affiliate links.
                </p>

                {tour.tour_platforms && tour.tour_platforms.length > 0 ? (
                  <div className="space-y-2.5">
                    {tour.tour_platforms.map((p) => (
                      <a
                        key={p.id}
                        href={p.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick(p.id)}
                        className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-accent border ${cheapestIds.has(p.id) ? "border-primary bg-accent/50" : "border-border bg-secondary/50"}`}
                      >
                        <span
                          className={`font-display font-bold text-lg ${cheapestIds.has(p.id) ? "text-primary" : "text-foreground"}`}
                        >
                          ${Number(p.price)}
                        </span>
                        <div className="flex-1">
                          <span className="text-sm font-semibold">{p.platform_name}</span>
                          {p.review_rating != null && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Star className="w-3 h-3 fill-warning text-warning" />
                              <span className="text-xs">{Number(p.review_rating).toFixed(1)}</span>
                              {p.review_count != null && (
                                <span className="text-xs text-muted-foreground">({p.review_count})</span>
                              )}
                            </div>
                          )}
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No booking links available yet.</p>
                )}

                {cheapestIds.size > 0 && (
                  <p className="text-[10px] text-primary font-medium mt-3">★ Best price highlighted</p>
                )}
              </div>
            </div>
          </div>

          {/* Left column */}
          <div className="lg:col-span-2 order-last lg:order-first space-y-10">
            {/* Tour Details */}
            <section>
              <h2 className="font-display text-xl font-bold text-foreground mb-4">Tour Details</h2>
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                {[
                  { label: "Category", value: tour.categories?.join(", "), icon: Anchor },
                  { label: "Boat Type", value: tour.boat_type, icon: Ship },
                  { label: "Duration", value: durationDisplay, icon: Clock },
                  { label: "Departure", value: tour.departure_point, icon: MapPin },
                  { label: "Pick Up", value: tour.pick_up, icon: MapPin },
                  { label: "Available To", value: tour.available_to?.join(", "), icon: Users },
                  { label: "Time of Day", value: tour.time_of_day?.join(", "), icon: Clock },
                ]
                  .filter((r) => r.value)
                  .map(({ label, value, icon: Icon }, i) => (
                    <div
                      key={label}
                      className={`flex items-center gap-3 px-5 py-3.5 ${i > 0 ? "border-t border-border" : ""}`}
                    >
                      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm text-muted-foreground w-28 flex-shrink-0">{label}</span>
                      <span className="text-sm font-medium text-foreground">{value}</span>
                    </div>
                  ))}
              </div>
            </section>

            {/* What People Say */}
            <section>
              <h2 className="font-display text-xl font-bold text-foreground mb-2">What People Say In The Reviews</h2>
              <p className="text-xs text-muted-foreground mb-5">This is a summary of real traveler reviews. To read the full reviews, click on one of the <button type="button" onClick={scrollToBooking} className="text-primary hover:underline lg:hidden">booking links</button><span className="hidden lg:inline">booking links</span>.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tour.vibe && (
                  <div className="bg-card rounded-xl border border-border p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Vibe</p>
                    <span className="text-sm font-medium bg-secondary text-secondary-foreground px-3 py-1 rounded-full">
                      {tour.vibe}
                    </span>
                  </div>
                )}
                {tour.crowd_size && (
                  <div className="bg-card rounded-xl border border-border p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Crowd Size</p>
                    <span className="text-sm font-medium bg-secondary text-secondary-foreground px-3 py-1 rounded-full">
                      {tour.crowd_size}
                    </span>
                  </div>
                )}
                {tour.boarding && (
                  <div className="bg-card rounded-xl border border-border p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Boarding</p>
                    <span className="text-sm font-medium bg-secondary text-secondary-foreground px-3 py-1 rounded-full">
                      {tour.boarding}
                    </span>
                  </div>
                )}
                {tour.best_for && tour.best_for.length > 0 && (
                  <div className="bg-card rounded-xl border border-border p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Best For</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {tour.best_for.map((b) => (
                        <span
                          key={b}
                          className="text-sm font-medium bg-secondary text-secondary-foreground px-3 py-1 rounded-full"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Rating attributes */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {RATING_ATTRS.map(({ key, label }) => {
                  const val = tour[key] as string | null;
                  const rating = parseRating(val);
                  const isNoAlcohol = key === "open_bar" && val?.includes("No alcohol");
                  const noLabel = val && !parseRating(val) ? val : null;
                  return (
                    <div key={key} className="bg-card rounded-xl border border-border p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{label}</p>
                      {rating !== null ? (
                        <div className="flex items-center justify-center gap-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Circle
                              key={i}
                              className={`w-4 h-4 ${i <= rating ? "fill-primary text-primary" : "text-primary"}`}
                            />
                          ))}
                        </div>
                      ) : noLabel ? (
                        <span className="text-xs text-muted-foreground">{noLabel}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {isNoAlcohol && rating !== null && (
                        <p className="text-[10px] text-muted-foreground mt-1">No alcohol</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Pros & Cons */}
            {((tour.pros && tour.pros.length > 0) || (tour.cons && tour.cons.length > 0)) && (
              <section>
                <h2 className="font-display text-xl font-bold text-foreground mb-5">Reviews Highlights</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {tour.pros && tour.pros.length > 0 && (
                    <div className="space-y-2">
                      {tour.pros.map((p, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {tour.cons && tour.cons.length > 0 && (
                    <div className="space-y-2">
                      {tour.cons.map((c, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Reviews Summary */}
            {tour.reviews_summary && (
              <section>
                <h2 className="font-display text-xl font-bold text-foreground mb-3">Reviews Summary</h2>
                <p className="text-sm text-foreground/80 leading-relaxed">{tour.reviews_summary}</p>
              </section>
            )}

            {/* Itinerary */}
            {tour.itinerary && tour.itinerary.length > 0 && (
              <section>
                <h2 className="font-display text-xl font-bold text-foreground mb-5">Itinerary</h2>
                <ol className="space-y-3">
                  {tour.itinerary.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground font-display font-bold text-xs flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm pt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Inclusions */}
            {tour.inclusions && tour.inclusions.length > 0 && (
              <section>
                <h2 className="font-display text-xl font-bold text-foreground mb-4">What's Included</h2>
                <div className="flex flex-wrap gap-2">
                  {tour.inclusions.map((item, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 text-sm bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full"
                    >
                      <Check className="w-3.5 h-3.5 text-success" /> {item}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Similar Tours */}
        <SimilarTours current={tour} allTours={allTours} />

        {/* All Boat Tours */}
        <AllBoatTours tours={allTours} currentId={tour.id} />
      </div>
      </main>

      {/* Mobile sticky Book Now button */}
      {showStickyBook && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-card border-t border-border px-4 py-3 safe-bottom">
          <button
            onClick={scrollToBooking}
            className="w-full bg-primary text-primary-foreground font-display font-bold text-sm py-3 rounded-xl hover:bg-primary/90 transition-colors"
          >
            Book Now {price !== null && `· From $${price}`}
          </button>
        </div>
      )}
    </div>
  );
}
